import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Form } from "@/components/form/form";
import { FormField } from "@/components/form/form-field/form-field";
import { SmartDateInputFormField } from "@/components/form/form-field/date/smart-date-input-form-field";
import { GenderSelectFormField } from "@/components/form/form-field/select/gender-select-form-field";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { useAuthGetCurrentUser } from "@/orval/auth";
import { useFamilyCreateFamily, useFamilyGetMyFamilies, useFamilyUpdateFamily } from "@/orval/family";
import { Gender, type MyFamilyDTO, type UpsertFamilyDTO } from "@/orval/model";
import { createFileRoute } from "@tanstack/react-router";
import { PlusIcon, Save, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FamilyContactsSection } from "./-components/family-adults-section";
import { ChildEmailManager } from "./-components/child-email-manager";

const initialValues: UpsertFamilyDTO = { name: "", children: [] };

const ADDITIONAL_INFORMATION_LABEL = "Additional student information and/or allergies";
const ADDITIONAL_INFORMATION_HEADER = "Student Info / Allergies";
const COLS = "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_140px_120px_minmax(0,2fr)_150px]";

const ChildInlineRow: React.FC<{ index: number; onRemoveRequest: () => void }> = ({ index, onRemoveRequest }) => {
  const { getValues } = useFormContext();

  const canEdit = getValues(`children.${index}.canEdit`) ?? true;
  const canRemove = getValues(`children.${index}.canRemove`) ?? true;
  const child = getValues(`children.${index}`) as MyFamilyDTO["children"][number];
  const identityLocked = canEdit === false;
  const minBirthday = new Date();
  minBirthday.setFullYear(minBirthday.getFullYear() - 20);

  const deleteBtn = canRemove ? (
    <Button
      size="icon"
      variant="outline"
      className="size-8 shrink-0 self-center border-border text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10"
      type="button"
      onClick={onRemoveRequest}
    >
      <Trash2Icon className="size-3.5" />
      <span className="sr-only">Remove child</span>
    </Button>
  ) : (
    <div className="size-8 self-center" />
  );

  return (
    <>
      <div className={cn("hidden md:grid gap-x-2 items-center py-2 px-3 border-b", COLS)}>
        <FormField
          name={`children.${index}.firstName`}
          inputProps={{ placeholder: "First name", disabled: identityLocked }}
          rules={{ required: true }}
        />
        <FormField
          name={`children.${index}.lastName`}
          inputProps={{ placeholder: "Last name", disabled: identityLocked }}
          rules={{ required: true }}
        />
        <SmartDateInputFormField
          name={`children.${index}.birthday`}
          outputDateFormat="yyyy-MM-dd"
          minDate={canEdit ? minBirthday : undefined}
          rules={{ required: true }}
          inputProps={{ disabled: identityLocked, placeholder: "MM/DD/YYYY" }}
        />
        <GenderSelectFormField name={`children.${index}.gender`} />
        <FormField
          name={`children.${index}.additionalInformation`}
          inputProps={{ placeholder: ADDITIONAL_INFORMATION_LABEL, maxLength: 500 }}
        />

        <ButtonGroup className="justify-self-end">
          {!child.email && (
            <ChildEmailManager
              childId={child.id}
              childName={`${child.firstName} ${child.lastName}`}
              email={child.email}
            />
          )}
          {deleteBtn}
        </ButtonGroup>
      </div>

      {child.email && (
        <div className="hidden border-b md:block">
          <ChildEmailManager
            childId={child.id}
            childName={`${child.firstName} ${child.lastName}`}
            email={child.email}
          />
        </div>
      )}

      <div className="md:hidden p-3 border-b">
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          <FormField
            label="First Name"
            name={`children.${index}.firstName`}
            inputProps={{ disabled: identityLocked }}
            rules={{ required: true }}
          />
          <FormField
            label="Last Name"
            name={`children.${index}.lastName`}
            inputProps={{ disabled: identityLocked }}
            rules={{ required: true }}
          />
          <SmartDateInputFormField
            label="Birthday"
            name={`children.${index}.birthday`}
            outputDateFormat="yyyy-MM-dd"
            minDate={canEdit ? minBirthday : undefined}
            rules={{ required: true }}
            inputProps={{ disabled: identityLocked }}
          />
          <GenderSelectFormField label="Gender" name={`children.${index}.gender`} />
          <div className="col-span-2">
            <FormField
              label={ADDITIONAL_INFORMATION_LABEL}
              name={`children.${index}.additionalInformation`}
              inputProps={{ placeholder: ADDITIONAL_INFORMATION_LABEL, maxLength: 500 }}
            />
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-3">
          {child.email ? (
            <div className="border-t">
              <ChildEmailManager
                childId={child.id}
                childName={`${child.firstName} ${child.lastName}`}
                email={child.email}
              />
            </div>
          ) : (
            <ChildEmailManager
              childId={child.id}
              childName={`${child.firstName} ${child.lastName}`}
              email={child.email}
            />
          )}

          {canRemove && (
            <Button variant="destructive" className="w-full justify-center" type="button" onClick={onRemoveRequest}>
              <Trash2Icon className="mr-2 size-4" />
              Delete Child
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

const ChildrenTable: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const { fields, append, remove } = useFieldArray<MyFamilyDTO>({ name: "children" });
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);

  const handleAddChild = () => {
    append({
      id: 0,
      firstName: "",
      lastName: "",
      birthday: "",
      gender: Gender.Male,
      additionalInformation: "",
      canEdit: true,
      canRemove: true,
    });
  };

  const handleConfirmRemove = () => {
    if (removingIndex !== null) {
      remove(removingIndex);
      setRemovingIndex(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 w-full">
        <h3 className="text-lg font-semibold">Children</h3>
      </div>

      <div className="rounded-md border overflow-hidden">
        <div
          className={cn(
            "hidden md:grid gap-x-2 px-3 py-2 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide",
            COLS,
          )}
        >
          <div>First Name</div>
          <div>Last Name</div>
          <div>Birthday</div>
          <div>Gender</div>
          <div>{ADDITIONAL_INFORMATION_HEADER}</div>
          <div />
        </div>

        {isLoading && <p className="py-4 text-center text-sm text-muted-foreground">Loading family details…</p>}
        {!isLoading && fields.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">No children added yet.</p>
        )}
        {!isLoading &&
          fields.map((field, index) => (
            <ChildInlineRow key={field.id} index={index} onRemoveRequest={() => setRemovingIndex(index)} />
          ))}

        <div className="bg-muted/20 p-2">
          <Button variant="outline" type="button" onClick={handleAddChild} className="w-full">
            <PlusIcon className="size-4 mr-1.5" />
            Add child
          </Button>
        </div>
      </div>

      <AlertDialog open={removingIndex !== null} onOpenChange={(open) => !open && setRemovingIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove child?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the child from your family. You can add them back at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row">
            <AlertDialogAction onClick={handleConfirmRemove}>Remove</AlertDialogAction>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const MyFamilyPage: React.FC = () => {
  const [isCreatingFamily, setIsCreatingFamily] = useState(false);

  const { data: family, isFetching } = useFamilyGetMyFamilies();
  const { data: currentUser } = useAuthGetCurrentUser();
  const { mutateAsync: createFamilyAsync } = useFamilyCreateFamily();
  const { mutateAsync: updateFamilyAsync } = useFamilyUpdateFamily();

  const onSubmit = async (data: UpsertFamilyDTO) => {
    if (isCreatingFamily) {
      const newFamily = await createFamilyAsync({ data });
      setIsCreatingFamily(false);
      return newFamily;
    } else if ("id" in data) {
      return await updateFamilyAsync({ data });
    }
  };

  const pageTitle = isCreatingFamily ? "Create a Family" : "My Family";
  const saveButtonText = isCreatingFamily ? "Create Family" : "Save Changes";
  const isLoadingFamilyDetails = isFetching && !family && !isCreatingFamily;

  return (
    <div className="container">
      <h2 className="text-3xl font-semibold tracking-tight py-8 border-b text-foreground">{pageTitle}</h2>

      {isFetching || family || isCreatingFamily ? (
        <Form<UpsertFamilyDTO>
          onSubmit={onSubmit}
          useFormProps={{ defaultValues: family || initialValues }}
          isFetching={isFetching}
        >
          <div className="mt-6 max-w-sm">
            <FormField
              label="Family name"
              name="name"
              inputProps={{ placeholder: "Your family name" }}
              rules={{ required: true }}
            />
          </div>

          <FamilyContactsSection
            adults={family?.adults ?? []}
            organizations={family?.additionalContactOrganizations ?? []}
            currentUserId={currentUser?.id}
            isLoading={isLoadingFamilyDetails}
          />

          <section className="mt-8 border-t pt-6">
            <ChildrenTable isLoading={isLoadingFamilyDetails} />
          </section>

          <div className="flex gap-3 mt-4 pb-8">
            {/* {isCreatingFamily && (
              <IconButton
                onClick={() => console.log("Join an existing family")}
                variant="secondary"
                icon={<UserPlus2Icon />}
                type="button"
              >
                Join an existing family
              </IconButton>
            )} */}
            <IconButton type="submit" icon={isCreatingFamily ? <PlusIcon /> : <Save />} className="ml-auto">
              {saveButtonText}
            </IconButton>
          </div>
        </Form>
      ) : (
        <div className="flex flex-col items-center justify-center py-24">
          <p className="text-center text-zinc-500 text-sm mb-12 mt-4">You aren't part of a family yet.</p>
          <div className="flex flex-col md:flex-row md:justify-center gap-4 w-full max-w-md">
            <IconButton onClick={() => setIsCreatingFamily(true)} icon={<PlusIcon />} className="flex-none">
              Create a family
            </IconButton>
            {/* <IconButton
              onClick={() => console.log("Join an existing family")}
              variant="secondary"
              icon={<UserPlus2Icon />}
              className="flex-none md:ml-auto"
              type="button"
            >
              Join an existing family
            </IconButton> */}
          </div>
        </div>
      )}
    </div>
  );
};

export const Route = createFileRoute("/_app/my/family")({
  component: MyFamilyPage,
});
